import type { ColDef, ICellRendererParams, IRowNode, ValueGetterParams } from 'ag-grid-enterprise'
import type { ReactElement } from 'react'
import type { ITodolistFlow } from '~/types/todolist'

import { TodolistStatusBadge } from '~/components/Badges'
import { CreatedByCellRenderer } from '~/components/CellRenderers/CreatedByCellRenderer'
import TextCellRenderer from '~/components/CellRenderers/TextCellRenderer'
import { TODOLIST_TASK_STATUS } from '~/types/todolist'
import { dateComparator } from '~/utils/date'
import { createdByColDef, lastUpdateColDefV2, textColDef } from '~/utils/table'

export interface IContext {
  application: string
  handleEditTodo: (data: ITodolistFlow) => void
  handleRedirect: (url: string) => void
}

const StatusCellRenderer = ({
  value,
}: ICellRendererParams<ITodolistFlow, TODOLIST_TASK_STATUS>): ReactElement | null => {
  return value ? <TodolistStatusBadge size="large" value={value} /> : null
}

const columns: ColDef[] = [
  textColDef(TextCellRenderer, {
    field: 'todolist.title',
    headerName: 'Name',
  }),
  {
    cellRenderer: StatusCellRenderer,
    field: 'status',
    filter: 'agSetColumnFilter',
    headerName: 'Status',
    headerTooltip: 'Status',
    menuTabs: ['filterMenuTab'],
    minWidth: 130,
    resizable: false,
    valueGetter: ({ data }: ValueGetterParams<ITodolistFlow>): string | undefined => {
      if (data) {
        const isPending = data.tasks.some(({ status }) => status === TODOLIST_TASK_STATUS.IN_PROGRESS)
        return isPending ? TODOLIST_TASK_STATUS.IN_PROGRESS : TODOLIST_TASK_STATUS.DONE
      }
      return undefined
    },
    width: 130,
  },
  createdByColDef(CreatedByCellRenderer, {
    field: 'todolist.created_by',
    suppressHeaderMenuButton: true,
    valueGetter: ({ data }) => data?.todolist || undefined,
  }),
  lastUpdateColDefV2({
    comparator: (
      _: unknown,
      __: unknown,
      { data: firstItem }: IRowNode<ITodolistFlow>,
      { data: secondItem }: IRowNode<ITodolistFlow>
    ): number => {
      if (firstItem && secondItem) {
        return dateComparator(firstItem.todolist.updated_at, secondItem.todolist.updated_at)
      }
      if (firstItem) {
        return -1
      }
      return 1
    },
    field: 'todolist.updated_by',
    suppressHeaderMenuButton: true,
    valueGetter: ({ data }) => data?.todolist || undefined,
  }),
]

export default columns
